import React from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';
import { CURRENCY_OPTIONS } from '../categories';

function ToggleRow({ label, description, checked, onChange, t, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: `1px solid ${t.borderLight}` }}>
      <div>
        <div style={{ fontSize: '14px', fontWeight: '500', color: disabled ? t.textFaint : t.text }}>{label}</div>
        {description && <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '2px' }}>{description}</div>}
      </div>
      <button
        onClick={() => !disabled && onChange(!checked)}
        aria-pressed={checked}
        disabled={disabled}
        style={{
          width: '44px', height: '24px', borderRadius: '12px', border: 'none',
          background: disabled ? t.borderMid : checked ? '#0061a4' : t.borderMid,
          cursor: disabled ? 'not-allowed' : 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.2s',
          padding: 0, opacity: disabled ? 0.5 : 1,
        }}
      >
        <span style={{
          position: 'absolute', top: '3px',
          left: checked ? '23px' : '3px',
          width: '18px', height: '18px', borderRadius: '50%',
          background: 'white', transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </button>
    </div>
  );
}

function ActionRow({ label, description, btnLabel, btnColor, onClick, t, locked, onUpgradeClick }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: `1px solid ${t.borderLight}` }}>
      <div>
        <div style={{ fontSize: '14px', fontWeight: '500', color: t.text }}>{label}</div>
        {description && <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '2px' }}>{description}</div>}
      </div>
      <button
        onClick={locked ? onUpgradeClick : onClick}
        style={{
          background: locked ? 'linear-gradient(135deg, #6a11cb, #2575fc)' : (btnColor || '#0061a4'),
          color: 'white', border: 'none',
          borderRadius: '8px', padding: '7px 16px', fontSize: '13px',
          fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
        }}
      >
        {locked ? 'Pro' : btnLabel}
      </button>
    </div>
  );
}

export default function SettingsView({ onExportJSON, onExportCSV, onReset, isAuthenticated, onSignInClick, isPro, onUpgradeClick, currency, onCurrencyChange, alertPrefs, onAlertPrefsChange }) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isDesktop = bp === 'desktop';
  const { t, isDark, toggle, bigText, setBigText, highContrast, setHighContrast, friendlyMode, setFriendlyMode } = useTheme();

  const pagePadding = isDesktop ? '32px 20px' : isMobile ? '20px 12px' : '24px 16px';

  const Section = ({ title, children, badge }) => (
    <div style={{ ...sectionStyle, background: t.card, boxShadow: t.shadow }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
        <h2 style={{ fontSize: '13px', fontWeight: '700', color: t.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', margin: 0 }}>
          {title}
        </h2>
        {badge && (
          <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', borderRadius: '10px', letterSpacing: '0.04em' }}>
            PRO
          </span>
        )}
      </div>
      {children}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: t.pageBg, padding: pagePadding }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: t.text, margin: 0 }}>Settings</h1>

        {/* Appearance */}
        <Section title="Appearance">
          <ToggleRow
            label="Light Mode"
            description="Switch between light and dark theme"
            checked={!isDark}
            onChange={() => toggle()}
            t={t}
          />
          <ToggleRow
            label="Bigger Text"
            description="Increases font size across the app"
            checked={bigText}
            onChange={setBigText}
            t={t}
          />
          <ToggleRow
            label="Friendly Language"
            description='Use plain-English labels (e.g. "Real Balance") instead of financial terminology (e.g. "Forecast")'
            checked={friendlyMode}
            onChange={setFriendlyMode}
            t={t}
          />
          <ToggleRow
            label="Higher Contrast"
            description="Sharpens text and border colors for readability"
            checked={highContrast}
            onChange={setHighContrast}
            t={t}
          />
        </Section>

        {/* Currency */}
        <Section title="Currency">
          <div style={{ padding: '14px 0', borderBottom: `1px solid ${t.borderLight}` }}>
            <div style={{ fontSize: '14px', fontWeight: '500', color: t.text, marginBottom: '8px' }}>Display Currency</div>
            <div style={{ fontSize: '12px', color: t.textMuted, marginBottom: '10px' }}>Display-only — no currency conversion is performed.</div>
            <select
              value={currency}
              onChange={(e) => onCurrencyChange(e.target.value)}
              style={{
                border: `1px solid ${t.borderFaint}`, borderRadius: '8px',
                padding: '9px 12px', fontSize: '14px', fontFamily: 'inherit',
                color: t.text, background: t.card, cursor: 'pointer', outline: 'none',
                width: '100%',
              }}
            >
              {CURRENCY_OPTIONS.map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>
        </Section>

        {/* Smart Alerts */}
        <Section title="Smart Alerts" badge={true}>
          {!isPro && (
            <div style={{ padding: '12px 0 8px', fontSize: '13px', color: t.textMuted, lineHeight: '1.5' }}>
              Get email notifications before bills are due and when your balance runs low.{' '}
              <button onClick={onUpgradeClick} style={{ background: 'none', border: 'none', color: '#0061a4', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', padding: 0 }}>
                Upgrade to Pro
              </button>{' '}to enable alerts.
            </div>
          )}
          <ToggleRow
            label="Bill Reminder — 3 days before"
            description="Email 3 days before a recurring bill is due"
            checked={alertPrefs?.billReminder3d ?? true}
            onChange={(v) => onAlertPrefsChange({ ...alertPrefs, billReminder3d: v })}
            t={t}
            disabled={!isPro}
          />
          <ToggleRow
            label="Bill Reminder — 1 day before"
            description="Email the day before a recurring bill is due"
            checked={alertPrefs?.billReminder1d ?? true}
            onChange={(v) => onAlertPrefsChange({ ...alertPrefs, billReminder1d: v })}
            t={t}
            disabled={!isPro}
          />
          <ToggleRow
            label="Low Balance Warning"
            description={`Alert when projected balance drops below ${currency} ${alertPrefs?.lowBalanceThreshold ?? 100}`}
            checked={alertPrefs?.lowBalance ?? true}
            onChange={(v) => onAlertPrefsChange({ ...alertPrefs, lowBalance: v })}
            t={t}
            disabled={!isPro}
          />
          <ToggleRow
            label="Monthly Summary"
            description="Receive a monthly overview of your income and spending on the 1st"
            checked={alertPrefs?.monthlySummary ?? true}
            onChange={(v) => onAlertPrefsChange({ ...alertPrefs, monthlySummary: v })}
            t={t}
            disabled={!isPro}
          />
          <ToggleRow
            label="Over-Budget Nudge"
            description="Alert when spending in any category exceeds your budget"
            checked={alertPrefs?.overBudget ?? true}
            onChange={(v) => onAlertPrefsChange({ ...alertPrefs, overBudget: v })}
            t={t}
            disabled={!isPro}
          />
          {isPro && (
            <div style={{ padding: '10px 0', fontSize: '12px', color: t.textMuted, lineHeight: '1.5', borderTop: `1px solid ${t.borderLight}` }}>
              Email delivery via Firebase Cloud Functions — coming soon. Alerts are saved and will activate when the backend is deployed.
            </div>
          )}
        </Section>

        {/* Data */}
        <Section title="Data">
          <ActionRow
            label="Export as JSON"
            description="Full backup of all your budget data"
            btnLabel="Export"
            btnColor="#0061a4"
            onClick={onExportJSON}
            locked={!isPro}
            onUpgradeClick={onUpgradeClick}
            t={t}
          />
          <ActionRow
            label="Export as CSV"
            description="Spreadsheet-compatible export of budget items, debts, and spending log"
            btnLabel="Export"
            btnColor="#0061a4"
            onClick={onExportCSV}
            locked={!isPro}
            onUpgradeClick={onUpgradeClick}
            t={t}
          />
          <ActionRow
            label="Reset All Data"
            description="Wipes everything and restarts the setup wizard"
            btnLabel="Reset"
            btnColor="#ba1a1a"
            onClick={onReset}
            t={t}
          />
        </Section>
      </div>
    </div>
  );
}

const sectionStyle = {
  background: 'white',
  borderRadius: '12px',
  padding: '4px 20px 4px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
};
